import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgxCurrencyConfig, NgxCurrencyDirective } from 'ngx-currency';
import { Subscription } from 'rxjs';

import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { UtilService } from '../../../../../shared/services/util.service';
import {
    TotaisDoDocumento,
    calcularTotaisDoDocumento,
    calcularTotalDoDocumento,
    contarItensAcimaDoTeto,
    fatorDescontoGlobal,
    valorDoDescontoPercentual,
} from './venda-totais.calculo';

/**
 * Seção 4 - Totais (§7). Os tributos apurados ficam na seção Tributos (`vd-tributos`).
 *
 * Fica logo abaixo dos Itens porque é deles que quase tudo aqui sai: os contadores, os pesos e o
 * total dos produtos são LEITURAS do mesmo FormArray. Só três campos se
 * digitam — desconto, frete e despesas —, e eles abrem a seção, separados dos demais por uma
 * régua, como manda a §7.
 *
 * **Os valores derivados são getters sobre o FormArray, e não campos sincronizados.** É o que
 * garante que a seção nunca mostre um número velho: mexeu no item, o próximo ciclo de detecção
 * já lê o valor novo, sem ninguém precisar lembrar de disparar recálculo. A CONTA mora em
 * `venda-totais.calculo.ts` e é a mesma que o `salvar()` do shell escreve nos controles
 * `valorIpi`, `valorIcms` e companhia — que continuam no form porque VIAJAM NO REQUEST.
 *
 * **Os campos derivados vão `disabled`, e não `readonly`** — é o que os pinta com a cor de campo
 * inativo, em vez de parecerem editáveis e não aceitarem digitação. Pode ser `disabled` sem
 * consequência porque eles são `[value]`, e não `formControlName`: controle desabilitado sairia
 * do `form.value` e sumiria do request, o que aconteceria se a ideia fosse aplicada nos TRÊS
 * campos editáveis — esses continuam com `readonly`, governado pelo `isSomenteLeitura`.
 *
 * @see venda-item.calculo.ts — os valores por item que alimentam estas somas.
 */
@Component({
    selector: 'app-vd-totais',
    standalone: true,
    imports: [
        AbntPipe,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        NgxCurrencyDirective,
        AlertCardComponent,
    ],
    templateUrl: './vd-totais.component.html',
    styleUrl: './vd-totais.component.css',
})
export class VdTotaisComponent implements OnInit, OnDestroy {
    @Input({ required: true }) public form!: FormGroup;
    @Input() public ehOrcamento: boolean = true;

    /** Teto de desconto do perfil (§6.5); `null` = sem teto. Para o aviso do desconto global. */
    @Input() public tetoDesconto: number | null = null;

    @Input({ required: true }) public mascaraPercentual!: Partial<NgxCurrencyConfig>;

    private assinaturas: Subscription[] = [];

    /**
     * Desconto do documento em R$ e em % (§7; decisão D5): os DOIS campos ficam sempre
     * preenchidos e um recalcula o outro, como valor × percentual no rateio de centro de custo.
     *
     * Digitou o %: `valorDesconto = Σ itens × % ÷ 100`, e o percentual passa a ser a FONTE.
     * Digitou o R$: o % recebe a equivalência e o R$ passa a ser a fonte. Itens mudaram: quem não
     * é a fonte acompanha. A fonte é estado SÓ DE TELA: os dois viajam sempre no request e o
     * servidor grava os dois sem recalcular (D20) — o R$ é o que vale.
     *
     * `emitEvent: false` nos dois sentidos — é o mesmo arranjo do preço × desconto do item.
     */
    public ngOnInit(): void {
        const valor = this.form.get('valorDesconto');
        const pct = this.form.get('percentualDescontoGlobal');
        const fonte = this.form.get('isDescontoPercentualFonte');
        const itens = this.form.get('itens');

        // Documento carregado em R$ (percentual gravado nulo): o % nasce com a equivalência para os
        // dois campos conversarem desde o início. Carregado em %, o shell já marcou a fonte.
        if (pct && (pct.value == null || pct.value === '') && Number(valor?.value ?? 0) > 0) {
            pct.setValue(this.percentualEquivalente, { emitEvent: false });
        }

        if (pct) {
            this.assinaturas.push(pct.valueChanges.subscribe((p) => {
                fonte?.setValue(true, { emitEvent: false });
                valor?.setValue(valorDoDescontoPercentual(this.form, Number(p ?? 0)), { emitEvent: false });
            }));
        }

        if (valor) {
            this.assinaturas.push(valor.valueChanges.subscribe(() => {
                fonte?.setValue(false, { emitEvent: false });
                pct?.setValue(this.percentualEquivalente, { emitEvent: false });
            }));
        }

        if (itens) {
            this.assinaturas.push(itens.valueChanges.subscribe(() => {
                if (fonte?.value === true) {
                    valor?.setValue(valorDoDescontoPercentual(this.form, Number(pct?.value ?? 0)), { emitEvent: false });
                } else {
                    pct?.setValue(this.percentualEquivalente, { emitEvent: false });
                }
            }));
        }
    }

    public ngOnDestroy(): void {
        this.assinaturas.forEach((a) => a.unsubscribe());
    }

    /** O % equivalente ao R$ atual sobre o total dos itens, 2 casas. */
    public get percentualEquivalente(): number {
        return UtilService.arredondarAbnt(fatorDescontoGlobal(this.form) * 100, 2);
    }

    /** Qual dos dois foi digitado: decide o hint e quem acompanha os itens. */
    public get percentualEhFonte(): boolean {
        return this.form.get('isDescontoPercentualFonte')?.value === true;
    }

    /** Quantos itens passam do teto com o desconto global composto (§6.5). */
    public get itensAcimaDoTeto(): number {
        return contarItensAcimaDoTeto(this.itens, this.tetoDesconto);
    }

    @Input() public isSomenteLeitura: boolean = false;

    /**
     * Dinheiro do DOCUMENTO, em 2 casas — e nao a mascara de PRECO da empresa (3 em dev).
     *
     * Desconto, frete e despesas nao sao preco de produto: sao valores a cobrar, e centavo e a
     * menor parte que existe. A configuracao de casas de preco rege o preco de lista e o preco
     * unitario do item, onde a fracao de centavo faz sentido para caber a unidade.
     */
    @Input({ required: true }) public mascaraDinheiro!: Partial<NgxCurrencyConfig>;

    public get itens(): FormArray {
        return this.form.get('itens') as FormArray;
    }

    // ======================================================== derivados dos itens

    /** Recalculado a cada leitura: é barato (dezenas de linhas) e nunca fica velho. */
    public get totais(): TotaisDoDocumento {
        return calcularTotaisDoDocumento(this.itens);
    }

    public get valorDesconto(): number {
        return this.numeroDoForm('valorDesconto');
    }

    public get valorFrete(): number {
        return this.numeroDoForm('valorFrete');
    }

    public get valorDespesas(): number {
        return this.numeroDoForm('valorDespesas');
    }

    /** §7: `itens + despesas + frete − desconto` — a MESMA conta que a cobertura do Pagamento lê. */
    public get totalDoDocumento(): number {
        return calcularTotalDoDocumento(this.form);
    }

    // ======================================================== textos por processo

    /** §7: o texto de apoio do frete e das despesas muda entre proposta e venda. */
    public get apoioFrete(): string {
        return this.ehOrcamento
            ? 'Somado ao total da proposta'
            : 'Somado ao total e considerado na nota fiscal';
    }

    public get apoioDespesas(): string {
        return this.ehOrcamento ? 'Somadas ao total da proposta' : 'Somadas ao total da venda';
    }

    // ======================================================== utilitários

    private numeroDoForm(campo: string): number {
        return Number(this.form?.get(campo)?.value ?? 0) || 0;
    }

}
